import { sha256 } from './sha256'

/** Pure-TS SHA-256 fallback for non-secure contexts (plain-http LAN hosts have no `crypto.subtle`); same output as Web Crypto (sha256.test.ts). */
export { sha256 }
/**
 * P0（身份/认证）——浏览器 SPA 对 DstMax 的 Authorization Code + PKCE 客户端（ADR-013/017/064；主计划 §12.9）。
 *
 * Purpose：给 DstBfs 各前端一套统一的登录/回调/续期/登出实现。public client，无 secret，
 * 全部凭据只落在注入的 `storage`（各 app 用自己的 sessionStorage）。`beginLogin` 只负责**拼出 authorize URL**，
 * 授权与租户归属的判定全在 DstMax，本文件不做任何授权决策。
 *
 * Invariants：
 * - `state` 与 `code_verifier` 一一对应，且都只存在于发起方这一个浏览器标签的 storage 里；`handleCallback` 必须
 *   `state === expected` 才继续换 token。
 * - `code_challenge_method` 恒为 `S256`（`s256()`：有 Web Crypto 用 subtle，明文 http 的 LAN 主机上退化到纯 TS 实现，
 *   两者输出相同——由 `sha256.test.ts` 断言）。
 * - 一个 access token 恰好对应一个租户（RLS 的唯一依据），本文件不提供任何本地改租户的入口。
 * - ADR-065 起**用户属于且只属于一个租户**，所以「换租户」= 换一个人登录，客户端这边没有对应的开关：
 *   `selectTenant` / `prompt=select_account` 已随选择页一起删除（O1-KR1.7-S06）。
 *   `tenant` 选项仍在，但它的用途只剩**登录入口带租户码**（`?tenant=demolive`），
 *   由 DstMax 在认证时使用；authorize 阶段服务端只拿它做一致性校验，对不上就要求重新登录。
 *
 * Authorization：本客户端不授权任何东西。`tenant` / `prompt` / `acr_values` 都只是**请求**，DstMax 可以拒绝；
 * 被拒时回调带 `error=...`，由调用方（`SignInEntry.vue`）呈现。access token 短（10 分钟），
 * refresh token 轮换，服务端检测重放。
 *
 * Transaction：不涉及数据库事务。`write()` 先 `removeItem` 五个 key 再整组写入，因此不会出现
 * 「新 access token 旁边留着旧 refresh token」这种半截状态；`write(null)` 清空本标签页的令牌；结束身份中心会话还必须整页导航至 `logout()` 返回的 URL。
 *
 * Idempotency：`beginLogin` 每次调用都新生成 verifier/state 并**覆盖**上一次的，所以同一时刻只有最近一次
 * authorize 请求能完成回调。`handleCallback` 分两种回调，行为**不同**，不要混为一谈：
 * - **带 `code`/`state` 的回调**：在校验之前就把 `pkce_state`/`pkce_verifier`/`return_to` 删掉，
 *   因此同一个成功回调 URL 再走一遍必然 `state mismatch` 抛错——重放拿不到第二个 token。
 * - **带 `error` 的回调**：`error` 分支在这三个 key 被删之前就抛出（见下方 `handleCallback` 第一条语句），
 *   所以重放一个错误回调只会**重复得到同一个 IdP 错误**，不会变成 `state mismatch`；
 *   与之对称的另一面是：一次被拒的回调**不会消耗掉**仍在等待中的那次 authorize 的 state/verifier。
 *   （Codex 2026-08-29 对 O12-KR12.12-S01 判 FAIL 的 finding 2 就是原注释把这两种情形写成了一种。）
 *
 * Failure：token 端点非 2xx 或缺 `access_token` → 抛 `Error`，带上 OAuth 的 `error`/`error_description`，不静默吞掉；
 * 回调带 `error` 同样抛出（`pkce.test.ts` 的「callback carrying an error from the IdP is surfaced」断言）。
 * `getAccessToken` 在没有 refresh token 或续期失败时 `write(null)` 并返回 `null`（当作已登出），不抛。
 * `logout` 先清本地令牌与待用 PKCE 值，再从 discovery 取得 end_session_endpoint；调用方必须整页导航至返回 URL。
 * revocation 是 best-effort 且不阻断导航；discovery 失败抛错（本地仍已清空，不能宣称身份中心已登出）。
 *
 * Verification：`web/packages/auth-pkce/test/pkce.test.ts`（S256 authorize URL + 回调换 token、IdP 错误上抛、
 * 上面 Idempotency 两条分支的**重放行为**各有一条断言、`stepUp` 发 `acr_values=mfa`+`prompt=login`、
 * `logout` 的 discovery / end-session URL / 无 id_token 降级 / best-effort revocation）、
 * `web/packages/auth-pkce/test/sha256.test.ts`（FIPS 180-4 向量与 Web Crypto 等价）、
 * `web/apps/dstbfs-web/test/e2e/tenant-switch.spec.ts`（URL 带租户码时透传给 authorize 的 `tenant` 参数）。
 * `logout` 的**服务端**撤销结果没有测试（本地只能观察到请求发出去了），Failure 段因此只写 best-effort。
 */
export interface KeyValueStorage { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }
export interface PkceClientOptions {
  issuer: string
  clientId: string
  redirectUri: string
  scopes: readonly string[]
  /** Storage key prefix, e.g. `dstbfs` → `dstbfs.access_token`; the SPAs keep their historical key names through this prefix. */
  storageKey: string
  storage: KeyValueStorage
  fetch?: typeof fetch
  now?: () => number
}
export interface TokenSet { idToken: string | null; accessToken: string; refreshToken: string | null; expiresAt: number; scope: string }
/** `stepUp`: re-authenticate with MFA now (§12.12.4 Step-up) — sends `acr_values=mfa` + `prompt=login` so DstMax asks for a fresh factor even with a live session; used before A3/A4 confirmations (KR7.3 S02). */
export interface BeginLoginOptions {
  tenant?: string | undefined
  returnTo?: string | undefined
  uiLocales?: string | undefined
  stepUp?: boolean | undefined
}

function base64url(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let s = ''
  for (const b of arr) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
/** S256 code challenge. Web Crypto exists only in secure contexts; on a plain-http LAN host (test deployment) fall back to the pure-TS digest. */
export async function s256(verifier: string): Promise<string> {
  const bytes = new TextEncoder().encode(verifier)
  const subtle = globalThis.crypto?.subtle
  return base64url(subtle ? await subtle.digest('SHA-256', bytes) : sha256(bytes))
}

export function createPkceClient(o: PkceClientOptions) {
  const f = o.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a))
  const now = o.now ?? (() => Date.now())
  const k = (name: string) => `${o.storageKey}.${name}`
  const issuer = o.issuer.replace(/\/$/, '')

  function read(): TokenSet | null {
    const access = o.storage.getItem(k('access_token'))
    if (!access) return null
    return { idToken: o.storage.getItem(k('id_token')), accessToken: access, refreshToken: o.storage.getItem(k('refresh_token')), expiresAt: Number(o.storage.getItem(k('expires_at')) ?? 0), scope: o.storage.getItem(k('scope')) ?? '' }
  }
  function write(t: TokenSet | null): void {
    for (const name of ['access_token', 'id_token', 'refresh_token', 'expires_at', 'scope']) o.storage.removeItem(k(name))
    if (!t) return
    o.storage.setItem(k('access_token'), t.accessToken)
    if (t.idToken) o.storage.setItem(k('id_token'), t.idToken)
    if (t.refreshToken) o.storage.setItem(k('refresh_token'), t.refreshToken)
    o.storage.setItem(k('expires_at'), String(t.expiresAt)); o.storage.setItem(k('scope'), t.scope)
  }
  async function exchange(body: Record<string, string>): Promise<TokenSet> {
    const res = await f(`${issuer}/connect/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' }, body: new URLSearchParams(body).toString() })
    const json = (await res.json()) as { access_token?: string; id_token?: string; refresh_token?: string; expires_in?: number; scope?: string; error?: string; error_description?: string }
    if (!res.ok || !json.access_token) throw new Error(json.error ? `${json.error}: ${json.error_description ?? ''}` : `token endpoint ${res.status}`)
    // Refresh responses may omit id_token; retain the login hint until the whole token set is cleared.
    const set: TokenSet = { idToken: json.id_token ?? (body['grant_type'] === 'refresh_token' ? read()?.idToken ?? null : null), accessToken: json.access_token, refreshToken: json.refresh_token ?? null, expiresAt: now() + (json.expires_in ?? 600) * 1000, scope: json.scope ?? o.scopes.join(' ') }
    write(set)
    return set
  }

  return {
    /** Generates verifier/state, stores them for the callback, and returns the authorize URL to navigate to. */
    async beginLogin(opts: BeginLoginOptions = {}): Promise<string> {
      const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)))
      const state = base64url(crypto.getRandomValues(new Uint8Array(16)))
      o.storage.setItem(k('pkce_verifier'), verifier); o.storage.setItem(k('pkce_state'), state); o.storage.setItem(k('return_to'), opts.returnTo ?? '/')
      const q = new URLSearchParams({ client_id: o.clientId, redirect_uri: o.redirectUri, response_type: 'code', scope: o.scopes.join(' '), code_challenge: await s256(verifier), code_challenge_method: 'S256', state })
      if (opts.tenant) q.set('tenant', opts.tenant)
      if (opts.uiLocales) q.set('ui_locales', opts.uiLocales)
      if (opts.stepUp) { q.set('acr_values', 'mfa'); q.set('prompt', 'login') }
      return `${issuer}/connect/authorize?${q.toString()}`
    },
    /** Handles the redirect back: validates state, exchanges the code, clears one-shot values; returns where to go next. */
    async handleCallback(callbackUrl: string): Promise<{ returnTo: string; tokens: TokenSet }> {
      const url = new URL(callbackUrl)
      const err = url.searchParams.get('error')
      if (err) throw new Error(`${err}: ${url.searchParams.get('error_description') ?? ''}`)
      const code = url.searchParams.get('code'); const state = url.searchParams.get('state')
      const expected = o.storage.getItem(k('pkce_state')); const verifier = o.storage.getItem(k('pkce_verifier'))
      const returnTo = o.storage.getItem(k('return_to')) ?? '/'
      for (const name of ['pkce_state', 'pkce_verifier', 'return_to']) o.storage.removeItem(k(name))
      if (!code || !state || !expected || !verifier || state !== expected) throw new Error('invalid_callback: state mismatch or missing code')
      const tokens = await exchange({ grant_type: 'authorization_code', client_id: o.clientId, code, redirect_uri: o.redirectUri, code_verifier: verifier })
      return { returnTo, tokens }
    },
    /** Current access token, refreshed (rotating refresh token) when within 60 s of expiry; null when signed out or refresh fails. */
    async getAccessToken(): Promise<string | null> {
      const t = read()
      if (!t) return null
      if (t.expiresAt - now() > 60_000) return t.accessToken
      if (!t.refreshToken) { write(null); return null }
      try { return (await exchange({ grant_type: 'refresh_token', client_id: o.clientId, refresh_token: t.refreshToken })).accessToken } catch { write(null); return null }
    },
    tokens: read,
    /** Clears local credentials immediately; returns the discovery end-session URL for a full-page navigation (never fetch logout). Without an ID token, client_id still binds the registered return URI. */
    async logout(): Promise<string> {
      const t = read(); write(null)
      for (const name of ['pkce_state', 'pkce_verifier', 'return_to']) o.storage.removeItem(k(name))
      // A slow/offline revocation must not hold the Cookie logout hostage. Keepalive lets it finish during navigation.
      if (t?.refreshToken) {
        void f(`${issuer}/connect/revocation`, { method: 'POST', keepalive: true, signal: AbortSignal.timeout(5_000), headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams({ token: t.refreshToken, client_id: o.clientId, token_type_hint: 'refresh_token' }).toString() }).catch(() => { /* best effort */ })
      }
      const res = await f(`${issuer}/.well-known/openid-configuration`, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(5_000) })
      const discovery = await res.json() as { end_session_endpoint?: string }
      if (!res.ok || !discovery.end_session_endpoint) throw new Error('end_session_endpoint unavailable')
      const url = new URL(discovery.end_session_endpoint)
      if (!['http:', 'https:'].includes(url.protocol)) throw new Error('invalid end_session_endpoint')
      url.searchParams.set('client_id', o.clientId)
      url.searchParams.set('post_logout_redirect_uri', `${new URL(o.redirectUri).origin}/`)
      url.searchParams.set('state', base64url(crypto.getRandomValues(new Uint8Array(16))))
      if (t?.idToken) url.searchParams.set('id_token_hint', t.idToken)
      return url.toString()
    },
  }
}
export type PkceClient = ReturnType<typeof createPkceClient>
